export const APPEARANCE_PREFERENCES = ["system", "light", "dark"] as const;

export type AppearancePreference = (typeof APPEARANCE_PREFERENCES)[number];

export type ResolvedAppearance = "light" | "dark";

export const UI_APPEARANCE_STORAGE_KEY = "rakazo.uiAppearance";

/**
 * Semantic palette shared by web, Electron, and Expo. Names follow the shadcn
 * convention so the same slot means the same thing on every surface: a `border`
 * is always a border and never a fill.
 */
export type ColorTokens = {
  background: string;
  /** Modals: a step lighter than the page so they read as raised above it. */
  dialog: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  /** Fill and hairline where native Liquid Glass is unavailable (Android, iOS before 26). */
  glassFill: string;
  glassStroke: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  /** The user's own chat bubbles; always the brand highlight, in both themes. */
  chatUser: string;
  /** White in both themes: the user bubble is a solid fill (light grey in dark, near-black in light). */
  chatUserForeground: string;
  muted: string;
  mutedForeground: string;
  /** Tertiary text: placeholders, list markers, quote bars, timestamps. */
  faintForeground: string;
  accent: string;
  accentForeground: string;
  /** Destructive text and outlines. */
  destructive: string;
  /** Solid destructive fill (Hang up, a filled Delete); its text is `destructiveForeground`. */
  destructiveFill: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarBorder: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  link: string;
  /** Backing behind a link's site icon. White in both themes so dark logos stay visible on any bubble. */
  faviconPlate: string;
  success: string;
  warning: string;
  statusWorking: string;
  /** Unread dot. The one blue in the inbox, in both themes. */
  unread: string;
  /** Working dot on a busy bot's avatar. The one green, in both themes. */
  workingDot: string;
  overlay: string;
  scrollbar: string;
  scrollbarHover: string;
};

/** BOORO Midnight Shift. Mirrors docs/product/Branding/booro-tokens.css. */
export const darkTokens = {
  background: "#0A1024",
  dialog: "#101831",
  foreground: "#F8F6EC",
  card: "#101831",
  cardForeground: "#F8F6EC",
  popover: "#101831",
  popoverForeground: "#F8F6EC",
  glassFill: "#17203A",
  glassStroke: "#F8F6EC26",
  primary: "#A7FF3F",
  primaryForeground: "#0A1024",
  secondary: "#17203A",
  secondaryForeground: "#F8F6EC",
  chatUser: "#A7FF3F",
  chatUserForeground: "#0A1024",
  muted: "#101831",
  mutedForeground: "#B7BED3",
  faintForeground: "#8D95AC",
  accent: "#17203A",
  accentForeground: "#F8F6EC",
  destructive: "#FF737D",
  destructiveFill: "#E5484D",
  destructiveForeground: "#0A1024",
  border: "#29334B",
  input: "#17203A",
  ring: "#C4B5FF",
  sidebar: "#101831",
  sidebarForeground: "#F8F6EC",
  sidebarBorder: "#29334B",
  sidebarAccent: "#17203A",
  sidebarAccentForeground: "#F8F6EC",
  link: "#A7FF3F",
  faviconPlate: "#FFFFFF",
  success: "#A7FF3F",
  warning: "#FFD166",
  statusWorking: "#C4B5FF",
  unread: "#C4B5FF",
  workingDot: "#A7FF3F",
  overlay: "rgba(10, 16, 36, 0.72)",
  scrollbar: "#29334B",
  scrollbarHover: "#3A4560",
} as const satisfies ColorTokens;

/** BOORO Day Shift. Warm cream surfaces, midnight ink, lime only for actions. */
export const lightTokens = {
  background: "#F7F5EF",
  dialog: "#FFFFFF",
  foreground: "#0A1024",
  card: "#FFFFFF",
  cardForeground: "#0A1024",
  popover: "#FFFFFF",
  popoverForeground: "#0A1024",
  glassFill: "#FFFFFF",
  glassStroke: "#0A102416",
  primary: "#A7FF3F",
  primaryForeground: "#0A1024",
  secondary: "#F0EEE7",
  secondaryForeground: "#0A1024",
  chatUser: "#A7FF3F",
  chatUserForeground: "#0A1024",
  muted: "#F0EEE7",
  mutedForeground: "#606477",
  faintForeground: "#80838F",
  accent: "#EDEAE1",
  accentForeground: "#0A1024",
  destructive: "#B52D3D",
  destructiveFill: "#E5484D",
  destructiveForeground: "#FFFFFF",
  border: "#E4E1D9",
  input: "#F0EEE7",
  ring: "#7751D3",
  sidebar: "#F2F0E8",
  sidebarForeground: "#0A1024",
  sidebarBorder: "#E4E1D9",
  sidebarAccent: "#FFFFFF",
  sidebarAccentForeground: "#0A1024",
  link: "#315C12",
  faviconPlate: "#FFFFFF",
  success: "#356D11",
  warning: "#966000",
  statusWorking: "#7751D3",
  // BOORO: light enough for ink digits on the unread badge.
  unread: "#9872FF",
  workingDot: "#356D11",
  overlay: "rgba(10, 16, 36, 0.45)",
  scrollbar: "#D8D4C9",
  scrollbarHover: "#BFBAAE",
} as const satisfies ColorTokens;

/** Web and Electron only: the primary fill's hover and disabled states. Mobile has no hover. */
export type WebColorTokens = ColorTokens & {
  primaryHover: string;
  primaryDisabled: string;
};

/**
 * Web and Electron values for the tokens they draw differently from the palettes above, which
 * follow the iOS system design for mobile. Mobile reads `darkTokens` / `lightTokens`; web reads
 * `tokens.css`, rendered from these merged palettes. Surfaces are opaque; text, borders, hover
 * and selection are the ink at an opacity, so they read the same on every surface.
 */
// BOORO: web and Electron show the same BOORO palette as mobile; only the lime
// primary needs its hover and disabled states.
export const webDarkOverrides = {
  primaryHover: "#94E62F",
  primaryDisabled: "#A7FF3F40",
} as const satisfies Partial<WebColorTokens>;

export const webLightOverrides = {
  primaryHover: "#94E62F",
  primaryDisabled: "#A7FF3F59",
} as const satisfies Partial<WebColorTokens>;

export const webDarkTokens: WebColorTokens = { ...darkTokens, ...webDarkOverrides };
export const webLightTokens: WebColorTokens = { ...lightTokens, ...webLightOverrides };

/** Dark palette. Prefer `tokensForAppearance` when theme-aware. */
export const tokens = darkTokens;

export const RADIUS = "0.75rem";

export const fontWeight = { regular: "400", medium: "500", semibold: "600" } as const;

/**
 * Native text styles on the system font, in points: iOS body text is 17/22. Titles use the
 * font's own line spacing, written out so they don't inherit a smaller body line height.
 */
export const typeScale = {
  title: { fontSize: 22, lineHeight: 26, fontWeight: fontWeight.semibold },
  headline: { fontSize: 19, lineHeight: 23, fontWeight: fontWeight.semibold },
  cardTitle: { fontSize: 17, lineHeight: 22, fontWeight: fontWeight.medium },
  body: { fontSize: 17, lineHeight: 22 },
  secondary: { fontSize: 15, lineHeight: 19 },
  caption: { fontSize: 14, lineHeight: 18 },
  footnote: { fontSize: 12, lineHeight: 16 },
} as const;

/**
 * Text grows with the iOS text size until body text reaches 23pt, the xxxLarge size. Larger
 * accessibility sizes keep that size, so message bubbles, rows and cards keep their layout.
 */
export const MAX_FONT_SCALE = 23 / typeScale.body.fontSize;

/** The ink of a drawn bot's eyes, the same on every body colour and in both themes. */
export const botEyeColor = "#101014";

/** Boo Crew identities from docs/product/Branding/booro-tokens.css (dark bodies). */
export const botColors = [
  "#F8F6EC",
  "#9872FF",
  "#78CCA0",
  "#FF914F",
  "#83CBE4",
  "#7998FF",
  "#F0CB57",
  "#B49989",
  "#F36F68",
  "#F16CB1",
  "#A0ACC4",
] as const;

export function isAppearancePreference(
  value: string | null | undefined,
): value is AppearancePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function normalizeAppearancePreference(
  raw: string | null | undefined,
): AppearancePreference {
  return isAppearancePreference(raw) ? raw : "system";
}

export type ResolveAppearancePreferenceOptions = {
  stored?: string | null;
  storage?: Pick<Storage, "getItem"> | null;
};

function getLocalStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function resolveAppearancePreference(
  options: ResolveAppearancePreferenceOptions = {},
): AppearancePreference {
  const stored =
    options.stored !== undefined
      ? options.stored
      : readStoredAppearance(options.storage ?? getLocalStorage());
  return normalizeAppearancePreference(stored);
}

export function persistAppearancePreference(
  preference: AppearancePreference,
  storage: Pick<Storage, "setItem"> | null = getLocalStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(UI_APPEARANCE_STORAGE_KEY, preference);
  } catch {
    // Ignore quota / private-mode failures; in-memory preference still applies.
  }
}

export function resolveAppearance(
  preference: AppearancePreference,
  system: ResolvedAppearance = "dark",
): ResolvedAppearance {
  if (preference === "system") return system;
  return preference;
}

export function tokensForAppearance(appearance: ResolvedAppearance): ColorTokens {
  return appearance === "light" ? lightTokens : darkTokens;
}

/** The web and Electron palette; `tokensForAppearance` is the mobile one. */
export function webTokensForAppearance(appearance: ResolvedAppearance): WebColorTokens {
  return appearance === "light" ? webLightTokens : webDarkTokens;
}

function readStoredAppearance(storage: Pick<Storage, "getItem"> | null | undefined): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(UI_APPEARANCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** `cardForeground` -> `--card-foreground` */
export function cssVariableName(token: keyof WebColorTokens): string {
  return `--${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

function renderBlock(selector: string, colorScheme: ResolvedAppearance, palette: WebColorTokens) {
  const lines = (Object.keys(palette) as (keyof WebColorTokens)[]).map(
    (token) => `  ${cssVariableName(token)}: ${palette[token].toLowerCase()};`,
  );
  return `${selector} {\n  color-scheme: ${colorScheme};\n${lines.join("\n")}\n  --radius: ${RADIUS};\n}`;
}

/** The CSS in `tokens.css` (web and Electron). Generated from the web palettes so both stay in sync. */
export function renderTokensCss(): string {
  return `${[
    "/* Generated by `pnpm --filter @rakazo/ui-tokens generate`. Edit src/index.ts instead. */",
    renderBlock(':root,\n[data-theme="dark"]', "dark", webDarkTokens),
    renderBlock('[data-theme="light"]', "light", webLightTokens),
  ].join("\n\n")}\n`;
}
